import React from 'react';
import { Alert, AlertTitle, Button, Box, Typography, CircularProgress } from '@mui/material';
import { ContentCopy as ImportIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformName } from '../config/platforms';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Importable {
  id: string;
  platform: string;
  account_name: string;
  team_id: string;
  workspace: string;
}

/**
 * Offer to reuse a connection another workspace already holds.
 *
 * For the case where one person administers several companies from a single Facebook or
 * LinkedIn login: the OAuth grant is the same and only the Page or Organization differs, so
 * re-running the flow asks the platform to re-authorise something it already has — which it
 * answers with "already connected" rather than a fresh grant.
 *
 * Shown only for platforms this workspace has not connected, so it never offers a duplicate,
 * and it says plainly that the token is shared. That is the real cost and it is not obvious:
 * disconnecting the source leaves this copy holding a token that will quietly stop working.
 */
export const ImportConnections: React.FC<{ onImported: () => void }> = ({ onImported }) => {
  const { token } = useAuth();
  const [available, setAvailable] = React.useState<Importable[]>([]);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState('');

  const headers = React.useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  const load = React.useCallback(() => {
    fetch(`${API_URL}/api/connections/importable`, { headers })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setAvailable(d?.available || []))
      .catch(() => undefined);
  }, [headers]);

  React.useEffect(() => { load(); }, [load]);

  const run = async (item: Importable) => {
    setBusy(item.id); setError('');
    try {
      const res = await fetch(`${API_URL}/api/connections/import`, {
        method: 'POST', headers,
        body: JSON.stringify({ fromTeamId: item.team_id, platform: item.platform }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not reuse that connection'); return; }
      load();
      onImported();
    } finally {
      setBusy(null);
    }
  };

  if (available.length === 0) return null;

  return (
    <Alert severity="info" icon={<ImportIcon />} sx={{ mb: 3 }}>
      <AlertTitle>Reuse a connection you already have</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        These are connected in your other workspaces. If it is the same login and you only need
        a different Page or company selected, reuse it rather than connecting again.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
        {available.map(item => (
          <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              disabled={busy !== null}
              startIcon={busy === item.id ? <CircularProgress size={14} /> : <ImportIcon />}
              onClick={() => run(item)}
            >
              Use {getPlatformName(item.platform)} from {item.workspace}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {item.account_name}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* The cost, stated where the decision is made rather than buried in a doc. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        The copy shares the original's access token. If you later disconnect or reconnect it in
        the other workspace, this one keeps a token that will eventually stop working and will
        need connecting properly. Nothing is selected to publish to — you choose that after.
      </Typography>

      {error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>{error}</Typography>}
    </Alert>
  );
};
